import { useState, useEffect, useCallback } from "react";
import {
  Users, Phone, UserCheck, PhoneOff, PhoneMissed, CalendarCheck, ClipboardCheck,
  XCircle, HelpCircle, RefreshCw, AlertTriangle, ChevronRight, Search, X, Eye
} from "lucide-react";
import ClientPipelineDetail from "./ClientPipelineDetail";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Icônes par étape
const ETAPE_ICONS = {
  lead: Phone,
  lead_r1_pris: CalendarCheck,
  lead_a_rappeler: PhoneMissed,
  ancien_lead_a_rappeler: PhoneMissed,
  ancien_lead_nrp: PhoneOff,
  lead_perdu_avant_r1: XCircle,
  confirmer_rdv: CalendarCheck,
  r1_audit: ClipboardCheck,
  lead_perdu_suite_r1: XCircle,
  lead_reflexion_suite_r1: HelpCircle,
  client_temp: Users,
  lead_hors_zone: AlertTriangle
};

export default function PipelineWidget({ onClientClick, compact = false }) {
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedEtape, setSelectedEtape] = useState(null);
  const [etapeClients, setEtapeClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/pipeline/stats`);
      if (resp.ok) {
        const data = await resp.json();
        setStats(data);
      }
    } catch (e) {
      console.error("Erreur chargement stats pipeline:", e);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/pipeline/alerts`);
      if (resp.ok) {
        const data = await resp.json();
        setAlerts(data.alerts || []);
      }
    } catch (e) {
      console.error("Erreur chargement alertes:", e);
    }
  }, []);

  const syncPipeline = async () => {
    setSyncing(true);
    try {
      const resp = await authFetch(`${API}/pipeline/sync`);
      if (resp.ok) {
        await loadStats();
        await loadAlerts();
      }
    } catch (e) {
      console.error("Erreur sync pipeline:", e);
    }
    setSyncing(false);
  };

  const loadEtapeClients = async (etapeId) => {
    setLoadingClients(true);
    try {
      const resp = await authFetch(`${API}/pipeline/clients?etape=${etapeId}&limit=50`);
      if (resp.ok) {
        const data = await resp.json();
        setEtapeClients(data.clients || []);
      }
    } catch (e) {
      console.error("Erreur chargement clients étape:", e);
    }
    setLoadingClients(false);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadAlerts()]);
      setLoading(false);
    };
    load();
  }, [loadStats, loadAlerts]);

  useEffect(() => {
    if (selectedEtape) {
      loadEtapeClients(selectedEtape);
    }
  }, [selectedEtape]);

  if (loading) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  const etapes = stats?.etapes || [];
  const total = stats?.total || 0;

  // Grouper les étapes par catégorie pour l'affichage
  const etapesAvantR1 = etapes.filter(e => ['lead', 'lead_r1_pris', 'lead_a_rappeler', 'ancien_lead_a_rappeler', 'ancien_lead_nrp', 'lead_perdu_avant_r1', 'confirmer_rdv'].includes(e.id));
  const etapesApresR1 = etapes.filter(e => ['r1_audit', 'lead_perdu_suite_r1', 'lead_reflexion_suite_r1'].includes(e.id));
  const etapesAutres = etapes.filter(e => ['client_temp', 'lead_hors_zone', 'annule_par_societe'].includes(e.id));

  // Fonction pour rendre une carte d'étape
  const renderEtapeCard = (etape) => {
    const Icon = ETAPE_ICONS[etape.id] || Users;
    const isSelected = selectedEtape === etape.id;
    const hasClients = etape.count > 0;
    
    return (
      <button
        key={etape.id}
        onClick={() => setSelectedEtape(isSelected ? null : etape.id)}
        disabled={!hasClients}
        className={`flex flex-col items-center p-3 rounded-lg transition-all min-w-[80px] ${
          isSelected 
            ? 'bg-indigo-500/20 border border-indigo-500/50 scale-105' 
            : hasClients 
              ? 'hover:bg-slate-700/50 border border-transparent' 
              : 'opacity-40 border border-transparent cursor-not-allowed'
        }`}
        data-testid={`etape-${etape.id}`}
      >
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
          style={{ backgroundColor: `${etape.color}30` }}
        >
          <Icon className="w-5 h-5" style={{ color: etape.color }} />
        </div>
        <span className="text-lg font-bold" style={{ color: etape.color }}>{etape.count}</span>
        <span className="text-[10px] text-slate-400 text-center leading-tight max-w-[70px]">
          {etape.nom}
        </span>
      </button>
    );
  };

  // Version compacte
  if (compact) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="pipeline-widget">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Où en est mon client ?</h3>
              <p className="text-xs text-slate-400">{total} clients dans le pipeline</p>
            </div>
          </div>
          <button
            onClick={syncPipeline}
            disabled={syncing}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            title="Synchroniser avec le Sheet"
            data-testid="sync-pipeline-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin text-indigo-400' : 'text-slate-400'}`} />
          </button>
        </div>

        {/* Étapes - Avant R1 */}
        <div className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Avant R1</p>
          <div className="flex flex-wrap gap-2">
            {etapesAvantR1.map(renderEtapeCard)}
          </div>
        </div>

        {/* Étapes - Après R1 */}
        <div className="px-4 pb-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Après R1</p>
          <div className="flex flex-wrap gap-2">
            {etapesApresR1.map(renderEtapeCard)}
          </div>
        </div>

        {/* Étapes - Autres */}
        {etapesAutres.length > 0 && etapesAutres.some(e => e.count > 0) && (
          <div className="px-4 pb-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Autres</p>
            <div className="flex flex-wrap gap-2">
              {etapesAutres.map(renderEtapeCard)}
            </div>
          </div>
        )}

        {/* Alertes */}
        {alerts.length > 0 && (
          <div className="px-4 pb-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium text-red-400">{alerts.length} alerte(s)</span>
              </div>
              <div className="space-y-1">
                {alerts.slice(0, 2).map((alert, idx) => (
                  <p key={idx} className="text-xs text-slate-400">{alert.message}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modal clients de l'étape */}
        {selectedEtape && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">
                    {etapes.find(e => e.id === selectedEtape)?.nom || selectedEtape}
                  </h3>
                  <p className="text-sm text-slate-400">
                    {etapeClients.length} client(s)
                  </p>
                </div>
                <button 
                  onClick={() => setSelectedEtape(null)} 
                  className="p-2 hover:bg-slate-700 rounded-lg"
                  data-testid="close-modal-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {loadingClients ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
                  </div>
                ) : etapeClients.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucun client dans cette étape</p>
                ) : (
                  <div className="space-y-2">
                    {etapeClients.map((client) => (
                      <div 
                        key={client.client_id}
                        className="p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                        onClick={() => setSelectedClientId(client.client_id)}
                        data-testid={`client-card-${client.client_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{client.nom} {client.prenom}</p>
                            <p className="text-sm text-slate-400">{client.etape_nom}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Dpt {client.dpt}</p>
                            <p className="text-xs text-slate-500">{client.telepro}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal détail client */}
        {selectedClientId && (
          <ClientPipelineDetail 
            clientId={selectedClientId}
            onClose={() => setSelectedClientId(null)}
            onStatusChanged={() => {
              loadStats();
              if (selectedEtape) loadEtapeClients(selectedEtape);
            }}
          />
        )}
      </div>
    );
  }

  // Version complète
  return (
    <div className="space-y-6" data-testid="pipeline-full">
      {/* Header avec stats */}
      <div className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-xl border border-indigo-500/30 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Où en est mon client ?</h2>
              <p className="text-slate-400">{total} clients dans le pipeline</p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Rechercher un client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm w-64"
                data-testid="search-input"
              />
            </div>
            <button
              onClick={syncPipeline}
              disabled={syncing}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg flex items-center gap-2"
              data-testid="sync-btn"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              Synchroniser
            </button>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4">
            <p className="text-sm text-slate-400">Leads</p>
            <p className="text-2xl font-bold text-blue-400">
              {(etapes.find(e => e.id === 'lead')?.count || 0) + 
               (etapes.find(e => e.id === 'lead_a_rappeler')?.count || 0) +
               (etapes.find(e => e.id === 'ancien_lead_a_rappeler')?.count || 0)}
            </p>
          </div>
          <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-4">
            <p className="text-sm text-slate-400">RDV planifiés</p>
            <p className="text-2xl font-bold text-purple-400">
              {(etapes.find(e => e.id === 'lead_r1_pris')?.count || 0) +
               (etapes.find(e => e.id === 'confirmer_rdv')?.count || 0)}
            </p>
          </div>
          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4">
            <p className="text-sm text-slate-400">R1 effectués</p>
            <p className="text-2xl font-bold text-emerald-400">
              {etapes.find(e => e.id === 'r1_audit')?.count || 0}
            </p>
          </div>
          <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
            <p className="text-sm text-slate-400">Perdus</p>
            <p className="text-2xl font-bold text-red-400">
              {(etapes.find(e => e.id === 'lead_perdu_avant_r1')?.count || 0) +
               (etapes.find(e => e.id === 'lead_perdu_suite_r1')?.count || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Pipeline visuel - Avant R1 */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Phase Prospection (Avant R1)</h3>
        <div className="flex flex-wrap gap-3">
          {etapesAvantR1.map((etape) => {
            const Icon = ETAPE_ICONS[etape.id] || Users;
            const percentage = total > 0 ? Math.round((etape.count / total) * 100) : 0;
            
            return (
              <button
                key={etape.id}
                onClick={() => setSelectedEtape(etape.id === selectedEtape ? null : etape.id)}
                className={`flex flex-col items-center p-4 rounded-xl transition-all min-w-[100px] ${
                  selectedEtape === etape.id 
                    ? 'bg-indigo-500/20 border-2 border-indigo-500 scale-105' 
                    : 'hover:bg-slate-700/50 border-2 border-transparent'
                }`}
                data-testid={`etape-full-${etape.id}`}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-2"
                  style={{ backgroundColor: etape.color }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <span className="text-2xl font-bold">{etape.count}</span>
                <span className="text-xs text-slate-400 text-center leading-tight">{etape.nom}</span>
                <span className="text-[10px] text-slate-500 mt-1">{percentage}%</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pipeline visuel - Après R1 */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Phase Qualification (Après R1)</h3>
        <div className="flex flex-wrap gap-3">
          {etapesApresR1.map((etape) => {
            const Icon = ETAPE_ICONS[etape.id] || Users;
            const percentage = total > 0 ? Math.round((etape.count / total) * 100) : 0;
            
            return (
              <button
                key={etape.id}
                onClick={() => setSelectedEtape(etape.id === selectedEtape ? null : etape.id)}
                className={`flex flex-col items-center p-4 rounded-xl transition-all min-w-[100px] ${
                  selectedEtape === etape.id 
                    ? 'bg-indigo-500/20 border-2 border-indigo-500 scale-105' 
                    : 'hover:bg-slate-700/50 border-2 border-transparent'
                }`}
                data-testid={`etape-full-${etape.id}`}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-2"
                  style={{ backgroundColor: etape.color }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <span className="text-2xl font-bold">{etape.count}</span>
                <span className="text-xs text-slate-400 text-center leading-tight">{etape.nom}</span>
                <span className="text-[10px] text-slate-500 mt-1">{percentage}%</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pipeline visuel - Autres */}
      {etapesAutres.length > 0 && etapesAutres.some(e => e.count > 0) && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Autres</h3>
          <div className="flex flex-wrap gap-3">
            {etapesAutres.map((etape) => {
              const Icon = ETAPE_ICONS[etape.id] || Users;
              const percentage = total > 0 ? Math.round((etape.count / total) * 100) : 0;
              
              return (
                <button
                  key={etape.id}
                  onClick={() => setSelectedEtape(etape.id === selectedEtape ? null : etape.id)}
                  className={`flex flex-col items-center p-4 rounded-xl transition-all min-w-[100px] ${
                    selectedEtape === etape.id 
                      ? 'bg-indigo-500/20 border-2 border-indigo-500 scale-105' 
                      : 'hover:bg-slate-700/50 border-2 border-transparent'
                  }`}
                  data-testid={`etape-full-${etape.id}`}
                >
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: etape.color }}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-2xl font-bold">{etape.count}</span>
                  <span className="text-xs text-slate-400 text-center leading-tight">{etape.nom}</span>
                  <span className="text-[10px] text-slate-500 mt-1">{percentage}%</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Alertes */}
      {alerts.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <h3 className="text-lg font-semibold text-red-400">Alertes prioritaires</h3>
          </div>
          <div className="grid gap-3">
            {alerts.map((alert, idx) => (
              <div key={idx} className="bg-slate-900/50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{alert.message}</p>
                  <p className="text-sm text-slate-500">{alert.count} client(s) concerné(s)</p>
                </div>
                <button className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center gap-1">
                  Voir <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clients de l'étape sélectionnée */}
      {selectedEtape && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
            <h3 className="font-semibold">
              {etapes.find(e => e.id === selectedEtape)?.nom} - {etapeClients.length} client(s)
            </h3>
            <button onClick={() => setSelectedEtape(null)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {loadingClients ? (
            <div className="p-8 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-500" />
            </div>
          ) : (
            <div className="divide-y divide-slate-700/50 max-h-96 overflow-y-auto">
              {etapeClients.map((client) => (
                <div 
                  key={client.client_id}
                  className="p-4 hover:bg-slate-800/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedClientId(client.client_id)}
                  data-testid={`client-row-${client.client_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium">
                          {client.nom?.[0]}{client.prenom?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{client.nom} {client.prenom}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-400">
                          <span>Dpt {client.dpt}</span>
                          <span>•</span>
                          <span>{client.etape_nom}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm">{client.telepro || '-'}</p>
                        <p className="text-xs text-slate-500">
                          {client.date_etape || "Date inconnue"}
                        </p>
                      </div>
                      <Eye className="w-5 h-5 text-slate-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal détail client */}
      {selectedClientId && (
        <ClientPipelineDetail 
          clientId={selectedClientId}
          onClose={() => setSelectedClientId(null)}
          onStatusChanged={() => {
            loadStats();
            if (selectedEtape) loadEtapeClients(selectedEtape);
          }}
        />
      )}
    </div>
  );
}
